import React, {useEffect} from "react";
import {Link} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {ButtonType, SpinnerButton} from "../../button/SpinnerButton";
import {ListItem} from "./Item";
import {
  deleteConfigurationAsync,
  downloadZipAsync,
  fetchListAsync,
  selectConfigurations,
  selectDeleteStatus,
  selectDownloadId,
  selectDownloadStatus,
  selectNoAccess,
  selectSearchQuery,
  selectSortDirection,
  selectSortStrategy,
  selectStatus,
  setSearchQuery,
  setSortDirection,
  setSortStrategy
} from "./listSlice";
import "./List.scss";
import {useAuth} from "react-oidc-context";
import {usePrevious} from "../../../infrastructure/hooks";
import {
  SortDirection,
  SortStrategy,
  noConfigurationsAvailable,
  noConfigurationsForQuery
} from "./constants";
import {sortHeaderClassName, sortedConfigurations} from "./helpers";
import {isIdle, isLoading} from "../../../infrastructure/status";
import {SortIcon} from "../../table/SortIcon";

export const List: React.FC = () => {
  const auth = useAuth();
  const allConfigurations = useAppSelector(selectConfigurations) ?? [];
  const downloadStatus = useAppSelector(selectDownloadStatus);
  const downloadId = useAppSelector(selectDownloadId);
  const status = useAppSelector(selectStatus);
  const deleteStatus = useAppSelector(selectDeleteStatus);
  const noAccess = useAppSelector(selectNoAccess);
  const dispatch = useAppDispatch();
  const query = useAppSelector(selectSearchQuery);
  const sortStrategy = useAppSelector(selectSortStrategy);
  const sortDirection = useAppSelector(selectSortDirection);
  const filteredConfigurations = allConfigurations.filter(configuration =>
    configuration.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
  );
  const configurations = sortedConfigurations(filteredConfigurations, sortStrategy, sortDirection);

  const onDelete = (id: number) => {
    dispatch(deleteConfigurationAsync(id));
  };
  const onDownload = (id: number) => {
    dispatch(downloadZipAsync(id));
  };

  useEffect(() => {
    auth?.isAuthenticated && !isLoading(status) && dispatch(fetchListAsync());
  }, [auth?.isAuthenticated]);

  const previousDeleteStatus = usePrevious(deleteStatus);

  useEffect(() => {
    isLoading(previousDeleteStatus) && isIdle(deleteStatus) && dispatch(fetchListAsync());
  }, [previousDeleteStatus, deleteStatus]);

  const onRefresh = async () => await dispatch(fetchListAsync());

  const sort = (strategy: SortStrategy) => {
    dispatch(setSortStrategy(strategy));
    sortStrategy === strategy
      ? dispatch(
          setSortDirection(
            sortDirection === SortDirection.ASCENDING
              ? SortDirection.DESCENDING
              : SortDirection.ASCENDING
          )
        )
      : dispatch(setSortDirection(SortDirection.ASCENDING));
  };

  return (
    <div className="container">
      <div className="configurations-container title">
        <h2>Mobile Configurations</h2>
      </div>
      <div className="configurations-container">
        <div id="form-inputs">
          <div className="row search-container">
            <div className="col-md-5 mb-3">
              {!noAccess && (
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type query..."
                  value={query}
                  onChange={event => dispatch(setSearchQuery(event.target.value))}
                />
              )}
            </div>
            <div className="col-md-5 mb-3 action-buttons">
              {!noAccess && (
                <Link to="add">
                  <button className="btn btn-primary btn-dark btn-medium">Add configuration</button>
                </Link>
              )}
              <SpinnerButton
                showSpinner={isLoading(status)}
                onClick={onRefresh}
                buttonType={ButtonType.SECONDARY}>
                Refresh
              </SpinnerButton>
            </div>
          </div>
          {configurations.length === 0 ? (
            isLoading(status) ? (
              <div className="loader-wrap light-background">
                <i className="fad fa-spinner-third fa-spin icon-xxl spinner spinner-page dark"></i>
              </div>
            ) : (
              <div className="row">
                <div className="">
                  <div className="block bg-sky-blue-lightest">
                    <div className="no-configurations">
                      <h4 className="no-configurations-text">
                        {noAccess
                          ? noConfigurationsAvailable
                          : query
                          ? noConfigurationsForQuery(query)
                          : noConfigurationsAvailable}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th
                    style={{whiteSpace: "nowrap"}}
                    className={sortHeaderClassName(sortStrategy, SortStrategy.ID)}
                    onClick={() => {
                      sort(SortStrategy.ID);
                    }}>
                    <div className="ellipsis-header">
                      Id{" "}
                      <SortIcon
                        currentStrategy={sortStrategy}
                        direction={sortDirection}
                        headerStrategy={SortStrategy.ID}
                      />
                    </div>
                  </th>
                  <th
                    style={{whiteSpace: "nowrap"}}
                    className={sortHeaderClassName(sortStrategy, SortStrategy.NAME)}
                    onClick={() => {
                      sort(SortStrategy.NAME);
                    }}>
                    <div className="ellipsis-header">
                      Name{" "}
                      <SortIcon
                        currentStrategy={sortStrategy}
                        direction={sortDirection}
                        headerStrategy={SortStrategy.NAME}
                      />
                    </div>
                  </th>
                  <th
                    style={{whiteSpace: "nowrap"}}
                    className={sortHeaderClassName(sortStrategy, SortStrategy.DATE)}
                    onClick={() => {
                      sort(SortStrategy.DATE);
                    }}>
                    <div className="ellipsis-header">
                      Date{" "}
                      <SortIcon
                        currentStrategy={sortStrategy}
                        direction={sortDirection}
                        headerStrategy={SortStrategy.DATE}
                      />
                    </div>
                  </th>
                  <th style={{whiteSpace: "nowrap"}}>
                    <div className="ellipsis-header">Actions</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="filter-row" style={{display: "none"}}>
                  <td></td>
                  <td>
                    <input className="form-control" />
                  </td>
                  <td>
                    <div className="table-filter-pills">
                      <div className="d-inline">
                        <span className="table-filter-pill">
                          <span>Option 1</span>
                          <i aria-hidden="true" tabIndex={1} className="table-filter-pill-icon"></i>
                        </span>
                        <span className="table-filter-pill">
                          <span>Option 4</span>
                          <i aria-hidden="true" tabIndex={1} className="table-filter-pill-icon"></i>
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <input className="form-control" />
                  </td>
                </tr>
                {configurations.map(configuration => (
                  <ListItem
                    key={configuration.id}
                    item={{
                      id: configuration.id,
                      date: new Date(configuration.lastUpdate).toLocaleString(),
                      name: configuration.name
                    }}
                    onDelete={onDelete}
                    onDownload={onDownload}
                    downloadStatus={configuration.id === downloadId ? downloadStatus : undefined}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
